/**
 * Clase que gestiona AudioContext + AnalyserNode + MediaElementSource.
 * Ver docs/SPEC-IMPLEMENTACION.md §5.
 */

import { calcularBandas, suavizar } from './bandas';
import type { BandasAudio, SuscriptorBandas } from './tipos';
import { BANDAS_INICIALES } from './tipos';

export class AnalizadorAudio {
  private contexto: AudioContext | null = null;
  private elementoAudio: HTMLAudioElement | null = null;
  private fuente: MediaElementAudioSourceNode | null = null;
  private analizador: AnalyserNode | null = null;
  private datosFrecuencia: Uint8Array<ArrayBuffer> | null = null;
  private bandasSuavizadas: BandasAudio = { ...BANDAS_INICIALES };
  private suscriptores = new Set<SuscriptorBandas>();
  private idFrame: number | null = null;
  private ruta: string;

  constructor(ruta: string) {
    this.ruta = ruta;
  }

  /**
   * Inicializa AudioContext y conecta los nodos.
   * Debe llamarse desde un gesto del usuario (gesture-required).
   */
  async iniciar(): Promise<void> {
    if (this.contexto) return;

    const ContextoAudio =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!ContextoAudio) {
      throw new Error('AudioContext no disponible en este navegador.');
    }

    // Configurar contexto de audio con latencia de reproducción adecuada para evitar buffer underruns
    this.contexto = new ContextoAudio({
      latencyHint: 'playback',
    });

    // Crear elemento de audio
    const audio = new Audio(this.ruta);
    audio.crossOrigin = 'anonymous';
    audio.preload = 'auto';
    this.elementoAudio = audio;

    // Esperar a que el audio esté listo para reproducir
    await new Promise<void>((resolve) => {
      let resuelto = false;
      const completar = () => {
        if (!resuelto) {
          resuelto = true;
          clearTimeout(timeoutId);
          audio.removeEventListener('canplay', completar);
          audio.removeEventListener('canplaythrough', completar);
          audio.removeEventListener('error', completar);
          resolve();
        }
      };

      const timeoutId = setTimeout(completar, 5000);
      audio.addEventListener('canplay', completar, { once: true });
      audio.addEventListener('canplaythrough', completar, { once: true });
      audio.addEventListener('error', completar, { once: true });
      audio.load();
    });

    if (!this.contexto) return;

    // Conectar nodos para análisis
    this.fuente = this.contexto.createMediaElementSource(audio);
    this.analizador = this.contexto.createAnalyser();
    this.analizador.fftSize = 256;
    this.analizador.smoothingTimeConstant = 0.5;
    this.datosFrecuencia = new Uint8Array(this.analizador.frequencyBinCount);

    this.fuente.connect(this.analizador);
    this.analizador.connect(this.contexto.destination);

    // Iniciar el frame loop
    this.bucle();
  }

  /**
   * Reproduce la canción. Si el contexto está suspendido, lo reanuda.
   */
  async reproducir(): Promise<void> {
    if (!this.contexto || !this.elementoAudio) {
      throw new Error('Analizador no inicializado. Llamar iniciar() primero.');
    }

    if (this.contexto.state === 'suspended') {
      await this.contexto.resume();
    }

    try {
      await this.elementoAudio.play();
    } catch (err) {
      console.warn('[AnalizadorAudio] No se pudo reproducir:', err);
      throw err;
    }
  }

  /**
   * Pausa la reproducción.
   */
  pausar(): void {
    this.elementoAudio?.pause();
  }

  /**
   * Detiene y rebobina.
   */
  detener(): void {
    if (this.elementoAudio) {
      this.elementoAudio.pause();
      this.elementoAudio.currentTime = 0;
    }
  }

  /**
   * Suscribe un callback que se invoca en cada frame con bandas suavizadas.
   * Retorna función para cancelar la suscripción.
   */
  suscribirse(fn: SuscriptorBandas): () => void {
    this.suscriptores.add(fn);
    return () => this.suscriptores.delete(fn);
  }

  /**
   * Obtiene las bandas actuales (síncrono).
   */
  obtenerBandas(): BandasAudio {
    return { ...this.bandasSuavizadas };
  }

  /**
   * Obtiene el tiempo actual de reproducción en segundos.
   */
  obtenerTiempoActual(): number {
    return this.elementoAudio?.currentTime ?? 0;
  }

  /**
   * Libera todos los recursos.
   */
  destruir(): void {
    if (this.idFrame !== null) {
      cancelAnimationFrame(this.idFrame);
      this.idFrame = null;
    }
    this.elementoAudio?.pause();
    this.fuente?.disconnect();
    this.analizador?.disconnect();
    this.contexto?.close().catch(() => undefined);
    this.suscriptores.clear();
    this.contexto = null;
    this.elementoAudio = null;
    this.fuente = null;
    this.analizador = null;
    this.datosFrecuencia = null;
  }

  /**
   * Frame loop interno: lee FFT, calcula bandas, suaviza y notifica.
   */
  private bucle = (): void => {
    if (!this.analizador || !this.datosFrecuencia) return;

    this.analizador.getByteFrequencyData(this.datosFrecuencia);
    const nuevasBandas = calcularBandas(this.datosFrecuencia);
    this.bandasSuavizadas = suavizar(this.bandasSuavizadas, nuevasBandas);

    for (const suscriptor of this.suscriptores) {
      suscriptor({ ...this.bandasSuavizadas });
    }

    this.idFrame = requestAnimationFrame(this.bucle);
  };
}
